/**
 * web/src/screens/Settings/about.tsx — the About section of Settings (card
 * MudceTexpbq1): Help + tour, the Leaving danger zone and the Build card —
 * moved verbatim out of index.tsx. State stays in the screen and arrives as
 * props.
 */
import type { Dispatch, SetStateAction } from 'react';
import { PartnerLabel, partnerLabel } from '../../lib/partnerName';
import { BUILD_ID, WHATS_NEW_FOR_BUILD } from './helpers';

export function AboutSection({
  onOpenHelp, onOpenTour, onSignOut, confirming, setConfirming, setLeaveError,
  leaving, confirmLeave, exporting, exportMsg, downloadMyData,
}: {
  onOpenHelp?: () => void;
  onOpenTour?: () => void;
  onSignOut?: () => void;
  confirming: boolean;
  setConfirming: Dispatch<SetStateAction<boolean>>;
  setLeaveError: Dispatch<SetStateAction<string | null>>;
  leaving: boolean;
  confirmLeave: () => Promise<void>;
  exporting: boolean;
  exportMsg: string | null;
  downloadMyData: () => Promise<void>;
}) {
  return (
    <>
        {/* ── About: leaving + build (the account-level rows) ─────────── */}
        {/* ── Help ───────────────────────────────────────────────────── */}
        {/* The full feature inventory (card FiLhF6LRwAAP) lives one tap from
            here: About is where people look when they are lost. */}
        {onOpenHelp && (
          <section className="set-section" aria-labelledby="set-help-title">
            <h3 className="set-section-title" id="set-help-title">
              Help
            </h3>
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Every feature, explained</div>
                <div className="set-note">
                  What each thing is, why it helps the two of you, and one
                  example of it in a real week.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn set-row-btn"
                id="btn-open-help"
                onClick={() => onOpenHelp()}
              >
                Open
              </button>
            </div>
            {/* The tour nests here (card yOszUUb0Nnc2): a ten-second walk
                through the app belongs with the explanation, not as a
                top-level row competing with the destinations themselves. */}
            {onOpenTour && (
              <div className="set-row">
                <div className="set-text">
                  <div className="set-label">Take the tour</div>
                  <div className="set-note">
                    Nine short stops through what Pear does, on demand. It
                    never opens by itself.
                  </div>
                </div>
                <button
                  type="button"
                  className="pear-btn set-row-btn"
                  id="btn-settings-tour"
                  onClick={() => onOpenTour()}
                >
                  Start
                </button>
              </div>
            )}
          </section>
        )}

        {/* ── Leaving ────────────────────────────────────────────────── */}
        <section className="set-section" aria-labelledby="set-danger-title">
          <h3 className="set-section-title set-section-title-danger" id="set-danger-title">
            Leaving
          </h3>

          {!confirming && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Sign out</div>
                <div className="set-note">
                  Ends your session on this device. The couple stays exactly as
                  it is — sign back in with your email and password.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn set-row-btn"
                id="btn-signout"
                onClick={() => onSignOut?.()}
              >
                Sign out
              </button>
            </div>
          )}

          {!confirming && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Leave this couple</div>
                <div className="set-note">
                  {PartnerLabel()} keeps the space. This account stops belonging to it
                  straight away — sign back in and you start from the pair screen.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn pear-btn-blush set-row-btn"
                id="btn-leave"
                onClick={() => { setLeaveError(null); setConfirming(true); }}
              >
                Leave
              </button>
            </div>
          )}

          {confirming && (
            <div className="set-confirm" role="group" aria-label="Confirm leaving">
              <p className="set-confirm-copy">
                Leave for good? You can’t undo this from here — rejoining needs a
                new pair code from {partnerLabel()}, and any Pear Plus subscription on
                this couple has to end first.
              </p>
              <div className="set-confirm-data">
                <div className="set-confirm-data-title">What happens to my data</div>
                <ul className="set-confirm-data-list">
                  <li>Your shared things — the thread, questions, gifts and dates — stay with {partnerLabel()}.</li>
                  <li>Your private things — private notes, private gift lists, your interests profile — are downloaded by you, then removed from this couple.</li>
                </ul>
              </div>
              <div className="set-confirm-actions">
                <button
                  type="button"
                  className="pear-btn set-cancel"
                  id="btn-export-data"
                  onClick={() => void downloadMyData()}
                  disabled={exporting || leaving}
                >
                  {exporting ? 'Building…' : 'Download my data first'}
                </button>
                {exportMsg && <p className="set-note" id="export-msg">{exportMsg}</p>}
                <button
                  type="button"
                  className="pear-btn set-cancel"
                  id="btn-leave-cancel"
                  onClick={() => { setConfirming(false); setLeaveError(null); }}
                >
                  Stay
                </button>
                <button
                  type="button"
                  className="pear-btn pear-btn-blush"
                  id="btn-leave-confirm"
                  onClick={() => void confirmLeave()}
                  disabled={leaving}
                >
                  {leaving ? 'Leaving…' : 'Leave couple'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Build freshness — the founder asked for an ongoing cache buster */}
        <section className="set-section" aria-labelledby="set-build-title">
          <h3 className="set-section-title" id="set-build-title">
            Build
          </h3>
          <div className="set-row">
            <div className="set-text">
              <div className="set-label" id="set-build-id">{BUILD_ID}</div>
              <div className="set-note">
                Not seeing a change you expected? Reload pulls the latest build.
              </div>
            </div>
            <button
              type="button"
              className="pear-btn set-row-btn"
              id="btn-reload-latest"
              onClick={() => window.location.assign(`/?cb=${Date.now()}`)}
            >
              Reload latest
            </button>
          </div>
          {/* Card QR9phRSRQHNx — what's new in this build, straight under the
              reference it belongs to. */}
          {WHATS_NEW_FOR_BUILD.map(entry => (
            <div className="set-whatsnew" key={entry.version + entry.date} data-testid="set-whatsnew">
              <p className="set-note" id="set-whatsnew-title">
                What&apos;s new · {entry.version === 'rolling' ? BUILD_ID : entry.version} ({entry.date})
              </p>
              <ul className="set-whatsnew-list">
                {entry.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
    </>
  );
}
